import React, { FC, useEffect, useRef, useState } from 'react'

import {
  CircleCheckmarkFullIcon,
  CircleFullIcon,
  IconSizes
} from '@inno-ui/icons'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { Trans, useTranslation } from 'react-i18next'
import { useGetCmResponsibleQuery } from 'shared/api/afterSalesApi'

import {
  getVerticalSlideTransition,
  verticalSlideVariants
} from '../../animation'
import { useDirectionalIndex } from '../../hooks/useDirectionalIndex'
import { HelloScreen } from '../HelloScreen'
import { MotionTourNav } from '../TourNav/TourNav'
import { VerticalSlide } from '../VerticalSlide'
import { GlassConfig, GlassConfigContext } from './GlassConfigContext'
import styles from './MainScreen.module.scss'
import { MainScreenProps, MainScreenStep } from './MainScreen.types'
import { ComplianceOverviewRightPanel } from './components/ComplianceOverviewRightPanel/ComplianceOverviewRightPanel'
import { ContactPersonRightPanel } from './components/ContactPersonRightPanel/ContactPersonRightPanel'
import { ContactTourCard } from './components/ContactTourCard/ContactTourCard'
import { GlassColumn } from './components/GlassColumn'
import { MonthlySignoffRightPanel } from './components/MonthlySignoffRightPanel/MonthlySignoffRightPanel'

const STEP_TRANSITION_DURATION = 0.38

export const MainScreen: FC<MainScreenProps> = ({
  onComplete,
  onOpenFullTour
}) => {
  const { data: cmResponsible } = useGetCmResponsibleQuery()

  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.full_page_onboarding.main_screen'
  })

  const highlighted = <span className={styles.highlighted} />

  const contactPersonStep: MainScreenStep | undefined = cmResponsible
    ? {
        id: 'contact-person',
        layout: 'split',
        secondaryTitle: t('steps.contact_person.secondary_title'),
        primaryTitle: (
          <Trans
            t={t}
            i18nKey='steps.contact_person.primary_title'
            components={{ highlighted }}
          />
        ),
        description: t('steps.contact_person.description'),
        listItems: t('steps.contact_person.list_items', {
          returnObjects: true
        }),
        rightPanel: () => (
          <ContactPersonRightPanel cmResponsible={cmResponsible} />
        ),
        fullWidthPanel: () => (
          <ContactTourCard onTakeFullTour={onOpenFullTour} />
        )
      }
    : undefined

  const steps: MainScreenStep[] = [
    {
      id: 'welcome',
      layout: 'centered',
      component: HelloScreen
    },
    {
      id: 'compliance-overview',
      layout: 'split',
      secondaryTitle: t('steps.compliance_overview.secondary_title'),
      primaryTitle: (
        <Trans
          t={t}
          i18nKey={'steps.compliance_overview.primary_title'}
          components={{ highlighted }}
        />
      ),
      description: t('steps.compliance_overview.description'),
      listItems: t('steps.compliance_overview.list_items', {
        returnObjects: true
      }),
      rightPanel: ComplianceOverviewRightPanel
    },
    {
      id: 'monthly-signoff',
      layout: 'split',
      secondaryTitle: t('steps.monthly_signoff.secondary_title'),
      primaryTitle: (
        <Trans
          t={t}
          i18nKey={'steps.monthly_signoff.primary_title'}
          components={{ highlighted }}
        />
      ),
      description: (
        <Trans
          t={t}
          i18nKey='steps.monthly_signoff.description'
          components={{
            strong: <strong className={styles.descriptionEmphasis} />
          }}
        />
      ),
      helper: (
        <Trans
          t={t}
          i18nKey='steps.monthly_signoff.helper'
          components={{ strong: <strong /> }}
        />
      ),
      rightPanel: MonthlySignoffRightPanel
    },
    ...(contactPersonStep ? [contactPersonStep] : [])
  ]

  const {
    index: activeStepIndex,
    direction,
    goToIndex
  } = useDirectionalIndex(steps.length)
  const activeStep = steps[activeStepIndex]

  const [glassConfig, setGlassConfig] = useState<GlassConfig | null>(null)

  const hasMountedRef = useRef(false)
  useEffect(() => {
    hasMountedRef.current = true
  }, [])

  return (
    <GlassConfigContext.Provider value={setGlassConfig}>
      <motion.div
        layout
        className={clsx(styles.mainScreen, {
          [styles.figmaCentered]: activeStep.layout === 'centered',
          [styles.figmaSplit]: activeStep.layout === 'split',
          [styles.figmaComplianceOverview]:
            activeStep.id === 'compliance-overview',
          [styles.figmaMonthlySignoff]: activeStep.id === 'monthly-signoff',
          [styles.figmaContact]: activeStep.id === 'contact-person'
        })}
        initial={false}
        transition={
          hasMountedRef.current
            ? getVerticalSlideTransition(STEP_TRANSITION_DURATION)
            : { duration: 0 }
        }
      >
        <VerticalSlide
          animateOnMount={false}
          slideKey={activeStep.layout}
          direction={direction}
          duration={STEP_TRANSITION_DURATION}
          className={styles.stepSlide}
        >
          {activeStep.layout === 'centered' ? (
            <activeStep.component
              onNext={() => goToIndex(activeStepIndex + 1)}
              onSkip={onComplete}
            />
          ) : (
            <div className={styles.splitLayout}>
              <div className={styles.splitRow}>
                <div className={styles.left}>
                  <div className={styles.content}>
                    <VerticalSlide
                      slideKey={activeStep.id}
                      direction={direction}
                      duration={STEP_TRANSITION_DURATION}
                      className={styles.contentSlide}
                    >
                      <div className={styles.titles}>
                        <h2 className={styles.secondaryTitle}>
                          {activeStep.secondaryTitle}
                        </h2>
                        <h1 className={styles.primaryTitle}>
                          {activeStep.primaryTitle}
                        </h1>
                      </div>
                      <p className={styles.description}>
                        {activeStep.description}
                      </p>
                      {activeStep.helper && (
                        <p className={styles.helper}>{activeStep.helper}</p>
                      )}
                      {activeStep.listItems && (
                        <ul className={styles.list}>
                          {activeStep.listItems.map((item, itemIndex) => {
                            const isRichItem = typeof item !== 'string'
                            const key = isRichItem ? item.label : item
                            const ListItemIcon = activeStep.listItemIconColors
                              ? CircleFullIcon
                              : CircleCheckmarkFullIcon
                            const itemIconColor =
                              activeStep.listItemIconColors?.[itemIndex] ??
                              '#BAE281'

                            return (
                              <li key={key} className={styles.item}>
                                <ListItemIcon
                                  color={itemIconColor}
                                  size={IconSizes.Size24}
                                  className={styles.itemIcon}
                                />
                                <span className={styles.itemText}>
                                  {isRichItem ? (
                                    <>
                                      <span className={styles.itemLabel}>
                                        {item.label}
                                      </span>{' '}
                                      {item.description}
                                    </>
                                  ) : (
                                    item
                                  )}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </VerticalSlide>
                  </div>
                </div>
                <div className={styles.right}>
                  <div className={styles.rightContent}>
                    <div className={styles.glassSlot}>
                      {glassConfig && (
                        <GlassColumn
                          layout
                          layoutDependency={activeStep.id}
                          style={{
                            left: glassConfig.left,
                            top: glassConfig.top,
                            width: glassConfig.width,
                            height: glassConfig.height
                          }}
                        />
                      )}
                    </div>
                    <AnimatePresence custom={direction}>
                      <motion.div
                        key={activeStep.id}
                        className={styles.rightSlide}
                        custom={direction}
                        variants={verticalSlideVariants}
                        initial='enter'
                        animate='center'
                        exit='exit'
                        transition={getVerticalSlideTransition(
                          STEP_TRANSITION_DURATION
                        )}
                      >
                        <activeStep.rightPanel />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
              {activeStep.fullWidthPanel && (
                <div className={styles.fullWidthPanel}>
                  <activeStep.fullWidthPanel />
                </div>
              )}
              <div className={styles.navigationWrapper}>
                <MotionTourNav
                  activeIndex={activeStepIndex}
                  ariaLabel={t('step_dots_label')}
                  backLabel={t('back')}
                  nextLabel={
                    activeStep.nextButtonLabel ||
                    (activeStepIndex === steps.length - 1
                      ? t('go_to_desktop')
                      : t('next'))
                  }
                  onBack={
                    activeStepIndex > 0
                      ? () => goToIndex(activeStepIndex - 1)
                      : undefined
                  }
                  onNext={() => {
                    if (activeStepIndex < steps.length - 1) {
                      goToIndex(activeStepIndex + 1)
                    } else {
                      onComplete()
                    }
                  }}
                  showBack={activeStepIndex > 0}
                  skipLabel={t('go_to_desktop')}
                  total={steps.length}
                  onSkip={onComplete}
                />
              </div>
            </div>
          )}
        </VerticalSlide>
      </motion.div>
    </GlassConfigContext.Provider>
  )
}
